import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, Phone, Mail } from "lucide-react";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_logistics-rajkot/artifacts/ry4xgme6_image.png";

const navLinks = [
  { name: "Home", path: "/" },
  { name: "About", path: "/about" },
  { name: "Services", path: "/services" },
  { name: "Gallery", path: "/gallery" },
  { name: "Contact", path: "/contact" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
  }, [location]);

  return (
    <>
      {/* Top bar */}
      <div className="bg-[#0F172A] text-white text-sm hidden md:block">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex justify-between items-center h-10">
          <span className="text-slate-300">From Skyline to Shoreline</span>
          <div className="flex items-center gap-6">
            <a href="tel:+917575873232" className="flex items-center gap-2 hover:text-[#F97316] transition-colors" data-testid="topbar-phone">
              <Phone size={14} strokeWidth={1.5} /> +91 75758 73232
            </a>
            <a href="mailto:info@pranavlogistics.com?subject=Inquiry%20-%20Pranav%20Logistics" className="flex items-center gap-2 hover:text-[#F97316] transition-colors" data-testid="topbar-email">
              <Mail size={14} strokeWidth={1.5} /> info@pranavlogistics.com
            </a>
          </div>
        </div>
      </div>

      {/* Main nav */}
      <nav
        className={`navbar-glass sticky top-0 z-50 ${scrolled ? "scrolled" : ""}`}
        data-testid="main-navbar"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16 md:h-20">
            <Link to="/" className="flex items-center gap-3" data-testid="navbar-logo">
              <img src={LOGO_URL} alt="Pranav Logistics Logo" className="h-12 md:h-14 w-auto" />
              <div className="hidden sm:block">
                <div className="font-['Barlow_Condensed'] font-bold text-[#0F172A] text-lg leading-tight tracking-tight">
                  PRANAV
                </div>
                <div className="font-['Barlow_Condensed'] text-[#0EA5E9] text-xs font-semibold tracking-widest">
                  LOGISTICS
                </div>
              </div>
            </Link>

            {/* Desktop links */}
            <div className="hidden md:flex items-center gap-1">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  data-testid={`nav-link-${link.name.toLowerCase()}`}
                  className={`px-4 py-2 rounded-md text-sm font-semibold transition-all duration-200 ${
                    location.pathname === link.path
                      ? "text-[#F97316] bg-orange-50"
                      : "text-[#0F172A] hover:text-[#F97316] hover:bg-slate-50"
                  }`}
                >
                  {link.name}
                </Link>
              ))}
              <Link
                to="/contact"
                className="ml-4 btn-primary text-sm !py-2.5 !px-6"
                data-testid="nav-get-quote-btn"
              >
                Get a Quote
              </Link>
            </div>

            {/* Mobile hamburger */}
            <button
              className="md:hidden p-2 text-[#0F172A]"
              onClick={() => setMobileOpen(!mobileOpen)}
              data-testid="mobile-menu-toggle"
            >
              {mobileOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>

        {/* Mobile menu */}
        {mobileOpen && (
          <div className="md:hidden bg-white border-t border-slate-200 animate-fade-in" data-testid="mobile-menu">
            <div className="px-4 py-4 space-y-1">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  data-testid={`mobile-nav-link-${link.name.toLowerCase()}`}
                  className={`block px-4 py-3 rounded-md text-sm font-semibold transition-colors ${
                    location.pathname === link.path
                      ? "text-[#F97316] bg-orange-50"
                      : "text-[#0F172A] hover:bg-slate-50"
                  }`}
                >
                  {link.name}
                </Link>
              ))}
              <Link
                to="/contact"
                className="block btn-primary text-sm text-center !py-3 mt-3"
                data-testid="mobile-get-quote-btn"
              >
                Get a Quote
              </Link>
              <div className="pt-3 border-t border-slate-100 mt-3 space-y-2 text-sm text-slate-600">
                <a href="tel:+917575873232" className="flex items-center gap-2">
                  <Phone size={14} /> +91 75758 73232
                </a>
                <a href="mailto:info@pranavlogistics.com?subject=Inquiry%20-%20Pranav%20Logistics" className="flex items-center gap-2">
                  <Mail size={14} /> info@pranavlogistics.com
                </a>
              </div>
            </div>
          </div>
        )}
      </nav>
    </>
  );
}
